import React from 'react';
import { SportsCard } from '../types';
import { Copy, CheckCircle2, TrendingUp, Bookmark, Star, Box } from 'lucide-react';

interface CardSlabProps {
    card: SportsCard;
    onList: (card: SportsCard) => void;
    isSaved?: boolean;
    onToggleSave?: (card: SportsCard) => void;
}

const CardSlab: React.FC<CardSlabProps> = ({ card, onList, isSaved = false, onToggleSave }) => {

    // Grade Color Logic
    let gradeColor = 'bg-slate-100 text-slate-700'; // Default Raw
    if (card.isGraded) {
        if (card.gradeScore === '10') gradeColor = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        else if (card.gradeScore === '9.5' || card.gradeScore === '9') gradeColor = 'bg-green-100 text-green-800 border-green-200';
        else gradeColor = 'bg-blue-100 text-blue-800 border-blue-200';
    }

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all border border-slate-200 flex flex-col h-full relative group">

            {/* Header / Label (Slab Style) */}
            <div className="bg-slate-50 border-b border-slate-200 p-3 flex justify-between items-center text-xs font-bold uppercase tracking-wide text-slate-500">
                <div className="flex flex-col">
                    <span className="text-[10px] leading-tight opacity-70">{card.year} {card.manufacturer}</span>
                    <span>{card.set}</span>
                </div>
                {card.cardNumber && <span className="text-slate-400">#{card.cardNumber}</span>}
            </div>

            {/* Save Button */}
            {onToggleSave && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSave(card);
                    }}
                    className={`absolute top-14 right-3 z-10 p-2 rounded-full shadow-sm border transition-all ${isSaved
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-500'
                        : 'bg-white/90 border-slate-200 text-slate-400 hover:text-blue-500 hover:border-blue-300'
                        }`}
                    title={isSaved ? "Add to Collection" : "Remove from Collection"}
                >
                    <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                </button>
            )}

            {/* Image Area */}
            <div className="bg-slate-100 h-64 flex items-center justify-center p-4 relative overflow-hidden">
                {card.thumbnailUrl ? (
                    <img
                        src={`data:image/jpeg;base64,${card.thumbnailUrl}`}
                        alt={card.player}
                        className="max-h-full max-w-full object-contain shadow-lg rounded-sm"
                    />
                ) : (
                    <div className="w-full h-full relative">
                        <img
                            src="/tradingcards/assets/vibe/009_the_liquid_glass_slab.jpeg"
                            alt="Raw Slab"
                            className="w-full h-full object-cover opacity-80 mix-blend-multiply"
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500/50 font-bold text-xs uppercase tracking-widest">
                            No Scan
                        </div>
                    </div>
                )}

                {/* Variant Badge */}
                {card.variant && card.variant !== 'Base' && (
                    <div className="absolute bottom-2 left-2 bg-purple-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm opacity-90">
                        {card.variant}
                    </div>
                )}
            </div>

            {/* Details Body */}
            <div className="p-4 flex-1 flex flex-col">

                {/* Title */}
                <h3 className="text-lg font-bold text-slate-900 leading-tight mb-1">{card.player}</h3>
                <p className="text-sm text-slate-500 mb-3">{card.team}</p>

                {/* Grade / Condition */}
                <div className="flex items-center gap-2 mb-4">
                    <div className={`px-2 py-1 rounded border text-xs font-bold ${gradeColor} flex items-center gap-1`}>
                        {card.isGraded ? <CheckCircle2 className="w-3 h-3" /> : <Star className="w-3 h-3" />}
                        {card.isGraded ? `${card.grader || 'Grade'} ${card.gradeScore}` : `Raw: ${card.gradeScore || 'Ungraded'}`}
                    </div>
                </div>

                {/* Subgrades / Condition Details */}
                <div className="bg-slate-50 rounded-lg p-2 mb-4 border border-slate-100 text-xs">
                    <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-slate-600">
                        <div className="flex justify-between">
                            <span className="text-slate-400">Centering</span>
                            <span className="font-medium">{card.conditionDetails.centering}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-400">Corners</span>
                            <span className="font-medium">{card.conditionDetails.corners}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-400">Edges</span>
                            <span className="font-medium">{card.conditionDetails.edges}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-400">Surface</span>
                            <span className="font-medium">{card.conditionDetails.surface}</span>
                        </div>
                    </div>
                </div>

                {/* FORENSIC SUB-REPORT */}
                {(card.conditionDetails.materialType || card.conditionDetails.printForensics) && (
                    <div className="bg-slate-900 rounded-lg p-3 mb-4 border border-slate-700 text-xs text-slate-300 relative overflow-hidden group/forensics">
                        {/* Subtle background texture */}
                        <div className="absolute inset-0 opacity-10 bg-[url('/assets/vibe/002_surface_topography_heatmap.jpeg')] bg-cover bg-center"></div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-2 border-b border-slate-700 pb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                <span className="font-bold text-blue-400 tracking-wider">FORENSIC LAB</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {card.conditionDetails.materialType && (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-1">
                                            <img src="/tradingcards/assets/vibe/004_geometric_corner_radius_analysis.jpeg" className="w-4 h-4 rounded-sm border border-slate-600 opacity-70" alt="Corner Tech" />
                                            <span className="text-[10px] uppercase text-slate-500 font-bold">Material</span>
                                        </div>
                                        <span className="font-mono text-white tracking-tight">{card.conditionDetails.materialType}</span>
                                    </div>
                                )}
                                {card.conditionDetails.printForensics && (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-1">
                                            <img src="/tradingcards/assets/vibe/003_the_rosette_zoom-in.jpeg" className="w-4 h-4 rounded-sm border border-slate-600 opacity-70" alt="Print Tech" />
                                            <span className="text-[10px] uppercase text-slate-500 font-bold">Process</span>
                                        </div>
                                        <span className="font-mono text-white tracking-tight leading-snug">{card.conditionDetails.printForensics}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Value Section */}
                <div className="mt-auto border-t border-slate-50 relative overflow-hidden rounded-b-xl group-hover:border-green-100 transition-colors">
                    {/* Market Graph Background */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity">
                        <img src="/tradingcards/assets/vibe/012_the_market_multiplier_graph.jpeg" className="w-full h-full object-cover" alt="Market Graph" />
                    </div>

                    <div className="p-3 relative z-10">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-0.5">Est. Value</p>
                        {card.estimatedValue.min === 0 && card.estimatedValue.max === 0 ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-slate-400">
                                    <TrendingUp className="w-4 h-4 opacity-50" />
                                    <span className="text-sm font-medium italic">
                                        Valuation Pending
                                    </span>
                                </div>
                                {card.valuationFailureReason && (
                                    <>
                                        <p className="text-xs text-slate-500">
                                            {card.valuationFailureReason === 'insufficient_sales_data' && '⚠ Insufficient recent sales data'}
                                            {card.valuationFailureReason === 'too_new' && '🆕 Too new - limited market history'}
                                            {card.valuationFailureReason === 'specialty_market' && '🌏 Requires specialty marketplace research'}
                                            {card.valuationFailureReason === 'variant_unclear' && '❓ Variant/edition unclear'}
                                            {card.valuationFailureReason === 'condition_assessment_needed' && '📸 Better images needed for assessment'}
                                        </p>
                                        <a
                                            href={`https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(`${card.player} ${card.year} ${card.manufacturer} ${card.set}`)}&LH_Sold=1&LH_Complete=1`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            Research on eBay
                                        </a>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-green-600">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-xl font-bold">
                                    ${card.estimatedValue.min} - ${card.estimatedValue.max}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* VERIFICATION SOURCES (INTGRITY CHECK) */}
                {card.verification_sources && card.verification_sources.length > 0 && (
                    <div className="mb-4">
                        <div className="flex items-center gap-1.5 mb-2">
                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Verified Sources</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {card.verification_sources.slice(0, 3).map((source, i) => (
                                <a
                                    key={i}
                                    href={source.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded truncate hover:bg-green-100 transition-colors max-w-[150px]"
                                    title={source.title}
                                >
                                    {source.title.length > 20 ? source.title.substring(0, 20) + '...' : source.title}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

            </div>

            {/* Actions */}
            <div className="p-3 bg-slate-50 border-t border-slate-200">
                <button
                    onClick={() => onList(card)}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                    <Copy className="w-4 h-4" />
                    List on eBay
                </button>
            </div>
        </div>
    );
};

export default CardSlab;